import { useEffect, useRef, useState } from 'react'
import { TraceRegion } from '../lib/trace'
import { ApiError, api, type Child } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button } from './ui'
import { SproutBuddy, parseBuddy } from './SproutBuddy'
import { BuddyMaker } from './BuddyMaker'
import { t } from '../lib/i18n'

/**
 * The picture on a child's card.
 *
 * Three kinds since 8 Oct 2026: a Sprout Buddy the child built, a drawn
 * picture, or a photo. The two that are not photos come first, deliberately.
 * Most families should never need to upload a picture of a child, so the
 * easy options are the ones that do not.
 *
 * If a photo IS uploaded, the server re-encodes it from raw pixels before it
 * touches disk. Phone photos carry EXIF, EXIF carries GPS, and for a picture
 * of a child that is usually their home address.
 */
export function Avatar({
  child, size = 56, version = 0,
}: { child: Child; size?: number; version?: number }) {
  const [broken, setBroken] = useState(false)
  const isPhoto = child.avatar_kind === 'photo' && !broken

  if (isPhoto) {
    return (
      <img
        src={`/api/children/${child.learner_id}/avatar?v=${version}`}
        alt=""
        onError={() => setBroken(true)}
        style={{
          width: size, height: size, borderRadius: radius.pill,
          objectFit: 'cover', flexShrink: 0, display: 'block',
        }}
      />
    )
  }

  const buddy = child.avatar_kind === 'drawn' ? parseBuddy(child.avatar_ref) : null
  if (buddy) {
    return (
      <div style={{
        width: size, height: size, borderRadius: radius.pill, flexShrink: 0,
        background: theme.greenPale, display: 'grid', placeItems: 'center', overflow: 'hidden',
      }}>
        <div style={{ marginTop: size * 0.08 }}>
          <SproutBuddy buddy={buddy} size={Math.round(size * 0.78)} animate={false} label="" />
        </div>
      </div>
    )
  }

  const drawn = child.avatar_kind === 'drawn' && child.avatar_ref
  return (
    <div style={{
      width: size, height: size, borderRadius: radius.pill, flexShrink: 0,
      background: theme.orangeWarm, color: theme.orangeDark,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * (drawn ? 0.5 : 0.43)), fontWeight: 800,
    }}>
      {drawn ? child.avatar_ref : child.display_name.slice(0, 1).toUpperCase()}
    </div>
  )
}

export function AvatarPicker({
  child, onChanged,
}: { child: Child; onChanged: (child: Child) => void }) {
  const [options, setOptions] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const [making, setMaking] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    api.drawnAvatars().then((r) => setOptions(r.emoji)).catch(() => setOptions([]))
  }, [])

  const pickDrawn = async (emoji: string) => {
    setBusy(true); setProblem(null)
    try {
      onChanged((await api.setDrawnAvatar(child.learner_id, emoji)).child)
    } catch (err) {
      setProblem(t(err instanceof Error ? err.message : 'That did not save.'))
    } finally {
      setBusy(false)
    }
  }

  const upload = async (file: File) => {
    setBusy(true); setProblem(null)
    try {
      onChanged((await api.uploadAvatar(child.learner_id, file)).child)
      setVersion((v) => v + 1)       // beat the browser cache
    } catch (err) {
      setProblem(t(err instanceof ApiError ? err.detail : 'That picture did not upload.'))
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  if (making) {
    return (
      <BuddyMaker
        grownUp
        child={child}
        onCancel={() => setMaking(false)}
        onSaved={(updated) => { setMaking(false); onChanged(updated) }}
      />
    )
  }

  return (
    <TraceRegion id="T2" writes note="a picture choice is saved as avatar_kind and avatar_ref on learner; a photo itself goes to T24 learner_photo">
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <Avatar child={child} size={64} version={version} />
        <div style={{ fontSize: 14, color: theme.faint, lineHeight: 1.5 }}>
          {child.avatar_kind === 'photo' ? t('A photo, seen only by your family.') : t('Pick a picture, or make a Sprout Buddy.')}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
        {options.map((emoji) => {
          const on = child.avatar_kind === 'drawn' && child.avatar_ref === emoji
          return (
            <button
              key={emoji}
              type="button"
              disabled={busy}
              onClick={() => pickDrawn(emoji)}
              style={{
                fontSize: 22, lineHeight: 1,
                background: on ? theme.orangePale : '#FFFFFF',
                border: `2px solid ${on ? theme.orange : theme.border}`,
                borderRadius: radius.sm, width: TAP_MIN, height: TAP_MIN,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {emoji}
            </button>
          )
        })}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void upload(file)
        }}
      />

      {/* A Sprout Buddy, a photo: the same kind of button, side by side
          (Sam, 8 Oct: the big buddy card did not look like a button). */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button tone="quiet" disabled={busy} onClick={() => setMaking(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingLeft: 14 }}>
          <span style={{ display: 'inline-block', marginTop: -6, marginBottom: -6 }}>
            <SproutBuddy size={26} animate={false} label="" buddy={parseBuddy(child.avatar_ref) ?? undefined} />
          </span>
          {parseBuddy(child.avatar_ref) ? t('Change Sprout Buddy') : t('Make a Sprout Buddy')}
        </Button>
        <Button tone="quiet" disabled={busy} onClick={() => fileInput.current?.click()}>
          {child.avatar_kind === 'photo' ? t('Change photo') : t('Add a photo')}
        </Button>
        {child.avatar_kind === 'photo' ? (
          <Button
            tone="ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              try { onChanged((await api.removeAvatar(child.learner_id)).child) }
              finally { setBusy(false) }
            }}
          >
            {t('Remove photo')}
          </Button>
        ) : null}
      </div>

      {problem ? (
        <p style={{ margin: '12px 0 0', fontSize: 15, color: theme.red }}>{problem}</p>
      ) : null}

      <p style={{ margin: '12px 0 0', fontSize: 13, color: theme.faint, lineHeight: 1.5 }}>
        {t('Photos are seen only by your family, with location details removed.')}

      </p>
    </div>
    </TraceRegion>
  )
}
